import { ServiceRequest } from "src/utils/service-request"
import { MOTOR_CADASTRO_VALIDACAO_PATH } from "../utils/api-routes"
import * as XLSX from 'xlsx-js-style'

class ValidacaoService {
    constructor() {
        this.request = new ServiceRequest()
    }

    async getUltimaValidacao(id, dataReferencia) {
        const safeId = encodeURIComponent(id)
        const query = new URLSearchParams({ data_referencia: dataReferencia })

        const path = `${MOTOR_CADASTRO_VALIDACAO_PATH}/${safeId}/validacoes?${query}`

        return this.request.get(path)
    }

    async postValidacao(id, payload) {
        const safeId = encodeURIComponent(id)
        const path = `${MOTOR_CADASTRO_VALIDACAO_PATH}/${safeId}/validacoes`
        return this.request.post(path, payload)
    }

    headerCell(value) {
        return {
            v: String(value ?? ""),
            t: "s",
            s: {
                fill: { patternType: "solid", fgColor: { rgb: "222851" } },
                font: { name: "Calibri", color: { rgb: "FFFFFF" }, sz: 11, bold: true },
                alignment: { wrapText: true, horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } },
                },
            },
        }
    }

    valueCell(value, type = "text") {
        let cellValue = value ?? ""

        if (type === "boolean") {
            if (value === true || value === 1 || value === "1") cellValue = "Sim"
            if (value === false || value === 0 || value === "0") cellValue = "Não"
        }

        const isNumber = typeof cellValue === "number" && Number.isFinite(cellValue)

        return {
            v: isNumber ? cellValue : String(cellValue),
            t: isNumber ? "n" : "s",
            ...(isNumber && type === "decimal" ? { z: "0.00000000" } : {}),
            s: {
                fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
                font: { name: "Calibri", color: { rgb: "000000" }, sz: 11 },
                alignment: { wrapText: true, horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } },
                },
            },
        }
    }

    getExcelColumnName(colIndex) {
        let dividend = colIndex + 1
        let columnName = ""

        while (dividend > 0) {
            const modulo = (dividend - 1) % 26
            columnName = String.fromCharCode(65 + modulo) + columnName
            dividend = Math.floor((dividend - modulo) / 26)
        }

        return columnName
    }

    createValidationWorksheet(title, columns, rows = []) {
        const worksheet = {}
        const lastColumnIndex = columns.length - 1
        const lastColumn = this.getExcelColumnName(lastColumnIndex)

        columns.forEach((column, index) => {
            const letter = this.getExcelColumnName(index)
            worksheet[`${letter}1`] = this.headerCell(index === 0 ? title : "")
            worksheet[`${letter}2`] = this.headerCell(column.headerName)
        })

        rows.forEach((row, rowIndex) => {
            columns.forEach((column, columnIndex) => {
                const letter = this.getExcelColumnName(columnIndex)
                worksheet[`${letter}${rowIndex + 3}`] = this.valueCell(
                    row[column.field], column.type
                )
            })
        })

        worksheet["!ref"] = `A1:${lastColumn}${Math.max(2, rows.length + 2)}`
        worksheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastColumnIndex } },
        ]
        worksheet["!cols"] = columns.map((column) => ({
            wch: column.excelWidth ?? Math.max(18, Math.min(42, Math.ceil((column.minWidth ?? 180) / 7))),
        }))
        worksheet["!rows"] = [{ hpt: 32 }, { hpt: 48 }]

        return worksheet
    }

    async exportValidationExcel({
        validationContext = {},
        parameters = {},
        results = {},
        premios = {},
        memories = {},
    }, memoryColumns) {
        const cetip = validationContext.cetip ?? validationContext.caracteristica?.CETIP
        const versao = validationContext.versaoDocumento
            ?? validationContext.caracteristica?.VersaoDocumento

        const hasValue = (value) => value !== null && value !== undefined && value !== ""
        const hasData = Object.values(results).some(hasValue)
            || hasValue(premios.recompra?.valor)
            || hasValue(premios.conversibilidade?.valor)
            || [memories.pupar, memories.pumtm, memories.fatorCorrecao,
            premios.conversibilidade?.memoria].some((rows) => rows?.length > 0)

        if (!hasData) {
            return {
                exported: false,
                hasEmptySheets: false,
            }
        }

        const workbook = XLSX.utils.book_new()
        let hasEmptySheets = false

        const appendSheet = (name, title, columns, rows) => {
            const hasSheetData = name === "Resultados"
                ? rows.some((row) => hasValue(row.valor))
                : rows.length > 0

            if (!hasSheetData) {
                hasEmptySheets = true
            }

            XLSX.utils.book_append_sheet(
                workbook,
                this.createValidationWorksheet(title, columns, rows),
                name
            )
        }
        const summaryColumns = [
            { field: "campo", headerName: "Campo", excelWidth: 36 },
            { field: "valor", headerName: "Valor", excelWidth: 30 },
        ]
        const toRows = (items) => items.map(([campo, valor]) => ({ campo, valor }))

        appendSheet("Resultados", "Resultados da Validação", summaryColumns, toRows([
            ["PUPAR calculado", results.puparCalculado],
            ["Delta PUPAR", results.deltaPupar],
            ["Variação percentual PUPAR", results.variacaoPercentualPupar],
            ["PUMTM calculado", results.pumtmCalculado],
            ["Delta PUMTM", results.deltaPumtm],
            ["Variação percentual PUMTM", results.variacaoPercentualPumtm],
            ["Spread emissão", results.spreadEmissao],
            ["Prazo médio emissão", results.prazoMedioEmissao],
            ["Prêmio de recompra", premios.recompra?.valor],
            ["Prêmio de conversibilidade", premios.conversibilidade?.valor],
            ["%PUPAR", results.percentualPupar],
            ["Duration modificada", results.durationModificada],
            ["Duration Macaulay", results.durationMacaulay],
        ]))

        appendSheet("MemoCalc_PUPAR", "Memória de Cálculo PUPAR", memoryColumns.pupar, memories.pupar ?? [])
        appendSheet("MemoCalc_PUMTM", "Memória de Cálculo PUMTM", memoryColumns.pumtm, memories.pumtm ?? [])
        appendSheet("MemoCalc_FCorr", "Memória de Cálculo Fator de Correção",
            memoryColumns.fatorCorrecao, memories.fatorCorrecao ?? [])

        if (premios.conversibilidade?.aplicavel) {
            appendSheet("MemoCalc_Convesibilidade", "Memória de Cálculo Prêmio de Conversibilidade",
                memoryColumns.conversibilidade, premios.conversibilidade.memoria ?? [])
        }

        if (premios.recompra?.aplicavel) {
            // todo: definir campos
            const illustrativeColumns = Array.from({ length: 5 }, (_, index) => ({
                field: `illustrative-repurchase-column-${index + 1}`,
                headerName: "",
            }))
            appendSheet("MemoCalc_Recompra", "Memória de Cálculo Prêmio de Recompra", illustrativeColumns, [])
        }

        const fileCetip = String(cetip || "cadastro").replace(/[\\/:*?"<>|]/g, "_")
        const fileVersion = String(versao ?? "sem-versao").replace(/[\\/:*?"<>|]/g, "_")
        const fileDate = String(parameters.dataReferencia || "sem-data").replace(/[\\/:*?"<>|]/g, "_")
        XLSX.writeFile(workbook, `Validacao_${fileCetip}_V${fileVersion}_${fileDate}.xlsx`)
        return {
            exported: true,
            hasEmptySheets,
        }
    }
}

export default ValidacaoService